import { useEffect, useState } from 'react'

import { useInterval } from '../../time'
import { autoExecuteAsync } from '../../async'

const useRequest = (
    requestFn: () => Promise<any>,
    {
        isDisabled = false,
        initialData = {},
        refreshInterval = null,
        shouldCallOnMount = true,
        onSuccessCallback = responseData => {
            return responseData
        },
        onErrorCallback = errorResponse => {
            return errorResponse
        },
    }: {
        isDisabled?: boolean
        initialData?: any
        refreshInterval?: number | null
        shouldCallOnMount?: boolean
        onSuccessCallback?: (responseData: any) => void
        onErrorCallback?: (errorResponse: any) => void
    } = {},
) => {
    const [data, setData] = useState(initialData)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState()

    const handleError = errorResponse => {
        setError(errorResponse)
        onErrorCallback(errorResponse)
    }

    const handleSuccess = response => {
        const responseData = response?.data ?? response

        setData(responseData)
        onSuccessCallback(responseData)
    }

    const callRequest = async () => {
        if (isDisabled) return

        try {
            setIsLoading(true)
            const response = await requestFn()

            if (response?.error !== null) {
                handleError(response.error)
            }

            handleSuccess(response)
        } catch (errorResponse) {
            handleError(errorResponse)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (shouldCallOnMount) {
            autoExecuteAsync(async () => {
                await callRequest()
            })
        }
    }, [])

    useInterval(callRequest, refreshInterval)

    return { data, isLoading, error, refetch: callRequest }
}

export { useRequest }
